import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCcw, Check, Trash, CheckCircle, Trash2, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import SearchBar from './SearchBar';
import ExportButton from './ExportButton';
import axios from 'axios';
import API_URL from '../../config/api';
import { filterBySearch, filterByStatus } from '../../utils/adminUtils';



const InquiriesPage = ({ showToast }) => {
    const [inquiries, setInquiries] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewInquiry, setViewInquiry] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, id: null });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;



    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter]);

    const normalizeInquiry = (item) => ({
        id: item.id,
        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        email: item.email ?? '',
        phone: item.phone ?? '',
        subject: item.subject ?? '',
        message: item.message ?? '',
        requirements: Array.isArray(item.requirements) ? item.requirements : [],
        status: item.status ?? 'New',
        createdAt: item.createdAt ?? null,
        updatedAt: item.updatedAt ?? null,
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token');

            const response = await axios.get(`${API_URL}/contact-inquiries`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const items = Array.isArray(response.data.data) ? response.data.data.map(normalizeInquiry) : [];

            setInquiries(items);
            setSelectedItems([]);
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
                return;
            }
            showToast?.('Failed to load inquiries', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const confirmDelete = (id) => {
        setDeleteConfirm({ isOpen: true, type: 'single', id });
    };

    const confirmBulkDelete = () => {
        if (selectedItems.length === 0) return;
        setDeleteConfirm({ isOpen: true, type: 'bulk', id: null });
    };

    const executeDelete = async () => {
        const { type, id } = deleteConfirm;
        setDeleteConfirm({ isOpen: false, type: null, id: null });

        if (type === 'single') {
            await performDeleteInquiry(id);
        } else if (type === 'bulk') {
            await performBulkDelete();
        }
    };

    const performDeleteInquiry = async (id) => {
        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(`${API_URL}/contact-inquiries/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setInquiries(prev => prev.filter(iq => iq.id !== id));
            setSelectedItems(prev => prev.filter(itemId => itemId !== id));
            showToast?.('Inquiry deleted successfully', 'success');
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
                return;
            }
            showToast?.('Failed to delete inquiry', 'error');
        }
    };

    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('admin_token');
            await axios.patch(`${API_URL}/contact-inquiries/${id}/read`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setInquiries(prev =>
                prev.map(iq => (iq.id === id ? { ...iq, status: 'Read' } : iq))
            );
            showToast?.('Inquiry marked as read', 'success');
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
                return;
            }
            showToast?.('Failed to mark inquiry as read', 'error');
        }
    };

    const performBulkDelete = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            await axios.post(`${API_URL}/contact-inquiries/bulk-delete`, { ids: selectedItems }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const remaining = inquiries.filter(iq => !selectedItems.includes(iq.id));
            setInquiries(remaining);
            setSelectedItems([]);
            showToast?.(`${selectedItems.length} inquiries deleted`, 'success');
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
                return;
            }
            showToast?.('Failed to delete selected inquiries', 'error');
        }
    };

    const bulkMarkAsRead = async () => {
        if (selectedItems.length === 0) return;

        try {
            const token = localStorage.getItem('admin_token');
            await axios.post(`${API_URL}/contact-inquiries/bulk-read`, { ids: selectedItems }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setInquiries(prev =>
                prev.map(iq =>
                    selectedItems.includes(iq.id) ? { ...iq, status: 'Read' } : iq
                )
            );
            setSelectedItems([]);
            showToast?.(`${selectedItems.length} inquiries marked as read`, 'success');
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('isAdminAuthenticated');
                window.location.href = '/admin/login';
                return;
            }
            showToast?.('Failed to mark selected inquiries as read', 'error');
        }
    };

    const filtered = useMemo(() => {
        let result = [...inquiries];

        if (searchQuery) {
            result = filterBySearch(result, searchQuery, ['firstName', 'lastName', 'email']);
        }

        if (statusFilter !== 'all') {
            result = filterByStatus(result, statusFilter);
        }

        return result;
    }, [inquiries, searchQuery, statusFilter]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filtered.slice(start, start + itemsPerPage);
    }, [filtered, currentPage]);

    const toggleSelectAll = () => {
        if (selectedItems.length === paginatedData.length && paginatedData.length > 0) {
            setSelectedItems([]);
        } else {
            setSelectedItems(paginatedData.map(iq => iq.id));
        }
    };

    const toggleSelectItem = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="w-full sm:w-96">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search inquiries..."
                    />
                </div>

                <div className="flex gap-2 items-center flex-wrap">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-dark-text text-sm focus:border-brand-teal focus:outline-none"
                    >
                        <option value="all" className="bg-white">All Status</option>
                        <option value="New" className="bg-white">New</option>
                        <option value="Read" className="bg-white">Read</option>
                    </select>

                    <ExportButton data={filtered} filename="inquiries" label="Export" />

                    <button
                        onClick={loadData}
                        disabled={loading}
                        className="p-2 hover:bg-slate-100 rounded-lg text-gray-400 transition-colors disabled:opacity-50"
                        title="Refresh"
                    >
                        <RefreshCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {selectedItems.length > 0 && (
                <div className="flex items-center gap-3 p-4 bg-brand-teal/10 border border-brand-teal/20 rounded-xl">
                    <span className="text-brand-teal font-medium">{selectedItems.length} selected</span>
                    <button
                        onClick={bulkMarkAsRead}
                        className="px-4 py-2 bg-brand-teal text-white rounded-lg font-medium hover:bg-brand-light transition-all flex items-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        Mark as Read
                    </button>
                    <button
                        onClick={confirmBulkDelete}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all flex items-center gap-2"
                    >
                        <Trash className="w-4 h-4" />
                        Delete
                    </button>
                </div>
            )}

            <div className="bg-white border border-slate-100 shadow-sm rounded-[32px] overflow-hidden">
                <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <h2 className="text-xl font-bold text-dark-text">Project Inquiries</h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
                                <th className="px-8 py-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.length === paginatedData.length && paginatedData.length > 0}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 rounded border-slate-300 bg-white checked:bg-brand-teal focus:ring-brand-teal"
                                    />
                                </th>
                                <th className="px-8 py-4 font-semibold">Client</th>
                                <th className="px-8 py-4 font-semibold">Requirements</th>
                                <th className="px-8 py-4 font-semibold">Status</th>
                                <th className="px-8 py-4 font-semibold">More</th>
                                <th className="px-8 py-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100">
                            {paginatedData.map((iq) => (
                                <tr key={iq.id} className="hover:bg-slate-50 group transition-colors">
                                    <td className="px-8 py-6">
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(iq.id)}
                                            onChange={() => toggleSelectItem(iq.id)}
                                            className="w-4 h-4 rounded border-slate-300 bg-white checked:bg-brand-teal focus:ring-brand-teal"
                                        />
                                    </td>

                                    <td className="px-8 py-6 text-dark-text">
                                        <div className="font-medium">
                                            {iq.firstName} {iq.lastName}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            <a href={`mailto:${iq.email}`} className="hover:text-brand-teal transition-colors" onClick={(e) => e.stopPropagation()}>
                                                {iq.email}
                                            </a>
                                        </div>
                                        {iq.phone && (
                                            <div className="text-xs text-slate-500 mt-1">
                                                <a href={`tel:${iq.phone}`} className="hover:text-brand-teal transition-colors" onClick={(e) => e.stopPropagation()}>
                                                    {iq.phone}
                                                </a>
                                            </div>
                                        )}
                                    </td>

                                    <td className="px-8 py-6">
                                        <div className="flex flex-wrap gap-1">
                                            {iq.requirements?.length > 0 ? (
                                                iq.requirements.map(req => (
                                                    <span
                                                        key={req}
                                                        className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-500"
                                                    >
                                                        {req}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-sm text-slate-400">No requirements</span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-8 py-6">
                                        <span
                                            className={`px-3 py-1 text-xs rounded-full ${iq.status === 'New'
                                                    ? 'bg-brand-teal/10 text-brand-teal'
                                                    : 'bg-gray-500/10 text-gray-500'
                                                }`}
                                        >
                                            {iq.status || 'New'}
                                        </span>
                                    </td>

                                    <td className="px-8 py-6">
                                        <button 
                                            onClick={() => setViewInquiry(iq)} 
                                            className="text-brand-teal hover:underline text-sm font-medium"
                                        >
                                            View
                                        </button>
                                    </td>

                                    <td className="px-8 py-6 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                            <button
                                                onClick={() => markAsRead(iq.id)}
                                                className="p-2 text-gray-500 hover:text-brand-teal transition-colors"
                                                title="Mark as read"
                                            >
                                                <CheckCircle className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => confirmDelete(iq.id)}
                                                className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filtered.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            {searchQuery || statusFilter !== 'all'
                                ? 'No inquiries match your filters'
                                : 'No inquiries yet'}
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {filtered.length > 0 && (
                    <div className="px-8 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div className="text-sm text-slate-500">
                            Showing <span className="font-medium text-dark-text">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                            <span className="font-medium text-dark-text">
                                {Math.min(currentPage * itemsPerPage, filtered.length)}
                            </span> of{' '}
                            <span className="font-medium text-dark-text">{filtered.length}</span> results
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                title="First Page"
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                title="Previous Page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            
                            <div className="flex items-center gap-1 px-2">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                                currentPage === pageNum
                                                    ? 'bg-brand-teal text-white shadow-sm'
                                                    : 'hover:bg-white border border-transparent hover:border-slate-200 text-slate-600'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                title="Next Page"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-slate-200 rounded-lg hover:bg-white disabled:opacity-50 disabled:hover:bg-transparent transition-all"
                                title="Last Page"
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* View Inquiry Modal */}
            {viewInquiry && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-dark-text">Inquiry Details</h3>
                            <button 
                                onClick={() => setViewInquiry(null)} 
                                className="p-2 text-slate-400 hover:text-dark-text hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="space-y-6">
                                <div>
                                    <span className="text-sm font-medium text-slate-500 block mb-1">Client Name</span>
                                    <div className="font-semibold text-lg text-dark-text">
                                        {viewInquiry.firstName} {viewInquiry.lastName}
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-sm font-medium text-slate-500 block mb-1">Email</span>
                                        <a href={`mailto:${viewInquiry.email}`} className="text-brand-teal hover:underline font-medium">
                                            {viewInquiry.email}
                                        </a>
                                    </div>
                                    {viewInquiry.phone && (
                                        <div>
                                            <span className="text-sm font-medium text-slate-500 block mb-1">Phone</span>
                                            <a href={`tel:${viewInquiry.phone}`} className="text-brand-teal hover:underline font-medium">
                                                {viewInquiry.phone}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {viewInquiry.subject && (
                                    <div>
                                        <span className="text-sm font-medium text-slate-500 block mb-1">Subject</span>
                                        <div className="text-dark-text font-medium">{viewInquiry.subject}</div>
                                    </div>
                                )}

                                <div>
                                    <span className="text-sm font-medium text-slate-500 block mb-1">Message</span>
                                    <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-xl text-sm text-dark-text border border-slate-100">
                                        {viewInquiry.message || 'No message provided.'}
                                    </div>
                                </div>

                                {viewInquiry.requirements?.length > 0 && (
                                    <div>
                                        <span className="text-sm font-medium text-slate-500 block mb-2">Requirements</span>
                                        <div className="flex flex-wrap gap-2">
                                            {viewInquiry.requirements.map(req => (
                                                <span 
                                                    key={req} 
                                                    className="px-3 py-1.5 bg-brand-teal/10 border border-brand-teal/20 rounded-full text-xs font-medium text-brand-teal"
                                                >
                                                    {req}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50/50">
                            <button 
                                onClick={() => setViewInquiry(null)} 
                                className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:text-dark-text font-medium transition-all shadow-sm"
                            >
                                Close
                            </button>
                            <a 
                                href={`mailto:${viewInquiry.email}`}
                                className="px-6 py-2.5 bg-brand-teal text-white rounded-xl hover:bg-brand-light font-medium transition-all shadow-sm flex items-center justify-center"
                            >
                                Reply via Email
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col transform transition-all">
                        <div className="p-6 text-center space-y-4">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto text-red-500">
                                <Trash2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold text-dark-text">Confirm Delete</h3>
                            <p className="text-slate-500 text-sm">
                                {deleteConfirm.type === 'bulk' 
                                    ? `Are you sure you want to delete ${selectedItems.length} selected inquiries?`
                                    : 'Are you sure you want to delete this inquiry?'}
                                <br/>This action cannot be undone.
                            </p>
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                            <button 
                                onClick={() => setDeleteConfirm({ isOpen: false, type: null, id: null })} 
                                className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium transition-all shadow-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={executeDelete}
                                className="px-5 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 font-medium transition-all shadow-sm flex items-center gap-2"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InquiriesPage;